import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { get, some } from "lodash";

import { useOrgFeatureFlagsForTenant } from "src/hooks/orgConfig";
import { listDesignWorkspaceIds as listDesignWorkspaceIdsQuery } from "src/pages/Designs/designs.graphql";

/**
 * Determines whether a workspace was provisioned for a design, i.e. it is
 * referenced as the `designWorkspaceId` of any design. Design workspaces have
 * their access managed by the design, so the access config is read-only.
 *
 * Only queries when the designs feature is enabled for the tenant.
 *
 * @param {string} [workspaceId]
 * @returns {{ isDesignWorkspace: boolean, loading: boolean }}
 */
export const useIsDesignWorkspace = (workspaceId) => {
  const { featureFlags } = useOrgFeatureFlagsForTenant();
  const enableDesigns = featureFlags?.designs?.enableDesigns ?? false;

  const { data, loading } = useQuery(listDesignWorkspaceIdsQuery, {
    skip: !enableDesigns || !workspaceId,
    fetchPolicy: "cache-and-network",
  });

  const isDesignWorkspace = useMemo(() => {
    if (!workspaceId) {
      return false;
    }
    return some(
      get(data, "designs", []),
      (design) => design?.designWorkspaceId === workspaceId
    );
  }, [data, workspaceId]);

  return { isDesignWorkspace, loading };
};

export default useIsDesignWorkspace;
